import { format } from '@monofront/business/utils';
import { IOrderItem, RoutesEnum } from '@monofront/types';
import ResponsiveImage from '@monofront/design-system/components/ResponsiveImage';
import { formatWeight } from '@monofront/business/utils';
import { useTranslations } from 'use-intl';
import Link from 'next/link';
import useLocalizedRoutes from '@hooks/i18n/useLocalizedRoutes';
import { trackEvent } from '@monofront/tracking';
import { isMobileApp } from '@utils/environment';
import { sendMessageToMobile } from '@utils/mobile';

interface IWrapperProps {
  children: React.ReactNode;
  href?: string | null;
  handle: string;
}

const Wrapper = ({ children, href, handle }: IWrapperProps) => {
  const onClickItem = (e) => {
    trackEvent({ type: 'LFProductClickedOrder' });
    if (isMobileApp) {
      e.preventDefault();
      e.nativeEvent.stopImmediatePropagation();
      sendMessageToMobile('goToProduct', { handle });
    }
  };

  return href ? (
    <Link onClick={onClickItem} href={href}>
      {children}
    </Link>
  ) : (
    <>{children}</>
  );
};

interface IOrderProductProps {
  orderProduct: IOrderItem;
  handle: string;
}

export const OrderProduct: React.FC<IOrderProductProps> = ({
  handle,
  orderProduct: { title, price, vendor, grams, productImages, quantity },
}) => {
  const tAccount = useTranslations('Account');
  const [{ src, alt } = { src: '', alt: 'Image not found' }] =
    productImages || [];
  const { getPath } = useLocalizedRoutes();

  return (
    <Wrapper
      href={handle ? `${getPath(RoutesEnum.PRODUCT_PAGE)}/${handle}` : null}
      handle={handle}
    >
      <div className="order-product tw-relative tw-mt-2 md:tw-mt-12">
        <div className="image tw-self-start">
          <ResponsiveImage src={src} alt={alt} width={1024} height={1024} />
        </div>
        <div className="content">
          <div className="info tw-leading-6">
            <div
              className={`title tw-lf-body-bold-md ${
                handle ? 'tw-lf-link-md tw-lf-link-dark' : ''
              }`}
            >
              {title}
            </div>
            <div className="vendor">{vendor}</div>
            <div className="weight">{formatWeight(grams / 1000)}</div>
            <span className="tw-lf-title-sm">{format(price)}</span>
          </div>

          <div className="subtotal tw-lf-title-sm">
            <span>{format(price * quantity)}</span>
          </div>

          <p className="quantity">{tAccount('quantitySmall', { quantity })}</p>
        </div>
        <style jsx>{`
          .order-product {
            display: flex;
            align-items: center;
            padding: var(--space-3) var(--space-4);
          }

          .content {
            display: grid;
            grid-template-columns: 1fr;
            grid-template-rows: 1fr;
            grid-template-areas: 'info quantity subtotal';
            width: 100%;
            align-items: center;
          }

          .image {
            height: 72px;
            width: 72px;
          }

          .image :global(img) {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }

          .info {
            grid-area: info;
            margin-left: var(--space-3);
          }

          .weight,
          .vendor {
            color: var(--clr-grey-1);
          }

          .subtotal {
            grid-area: subtotal;
            min-width: 70px;
            text-align: right;
          }

          .price {
            font-size: var(--font-size-3);
            font-family: var(--font-family-1);
          }

          .quantity {
            grid-area: quantity;
            margin: 0 var(--space-5);
          }

          @media (max-width: 800px) {
            .order-product {
              padding: var(--space-3) 0;
            }
            .content {
              grid-template-areas: 'info quantity', 'quantity quantity';
            }

            .quantity {
              font-size: var(--font-size-2);
              font-weight: bold;
              margin: 0;
            }

            .subtotal {
              display: none;
            }
          }
        `}</style>
      </div>
    </Wrapper>
  );
};
